import { useState } from 'react';

import { CheckCircle } from 'lucide-react';

import { PolicyReviewResponse } from '@/types/service/policyUserReview';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

interface AcknowledgeReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  review: PolicyReviewResponse | null;
  onConfirm: (reviewId: number) => Promise<void>;
  isAcknowledging: boolean;
}

const AcknowledgeReviewDialog = ({
  open,
  onOpenChange,
  review,
  onConfirm,
  isAcknowledging,
}: AcknowledgeReviewDialogProps) => {
  const [hasRead, setHasRead] = useState(false);

  if (!review) return null;

  const handleConfirm = async () => {
    if (!hasRead) return;

    try {
      await onConfirm(review.id);
      setHasRead(false); // Reset for next time
      onOpenChange(false);
    } catch (error) {
      // Error handled by parent component
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setHasRead(false); // Reset when dialog closes
    }
    onOpenChange(newOpen);
  };

  const reviewPeriodStart = new Date(review.review_period_start).toLocaleDateString();
  const reviewPeriodEnd = new Date(review.review_period_end).toLocaleDateString();

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='max-w-3xl max-h-[80vh]'>
        <DialogHeader>
          <div className='flex items-center gap-2'>
            <CheckCircle className='h-5 w-5 text-green-600' />
            <DialogTitle>Acknowledge Policy Review</DialogTitle>
          </div>
          <DialogDescription>
            Please review the policy content below and acknowledge that you have read and understood
            it.
          </DialogDescription>
        </DialogHeader>

        {/* Review Metadata */}
        <div className='bg-muted p-4 rounded-lg space-y-2 text-sm'>
          <div>
            <span className='font-medium'>Policy:</span> <span>{review.policy_name}</span>
          </div>
          {review.group_name && (
            <div>
              <span className='font-medium'>Group:</span> <span>{review.group_name}</span>
            </div>
          )}
          <div>
            <span className='font-medium'>Review Period:</span>{' '}
            <span>
              {reviewPeriodStart} - {reviewPeriodEnd}
            </span>
          </div>
        </div>

        {/* Policy Content */}
        <div className='space-y-2'>
          <h4 className='font-semibold text-sm'>Policy Content:</h4>
          <ScrollArea className='h-[300px] w-full border rounded-lg p-4'>
            <div
              className='prose prose-sm max-w-none'
              dangerouslySetInnerHTML={{ __html: review.policy_content }}
            />
          </ScrollArea>
        </div>

        {/* Acknowledgment Checkbox */}
        <div className='flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200'>
          <Checkbox
            id='acknowledge-checkbox'
            checked={hasRead}
            onCheckedChange={(checked) => setHasRead(checked === true)}
            disabled={isAcknowledging}
          />
          <label
            htmlFor='acknowledge-checkbox'
            className='text-sm font-medium leading-none cursor-pointer select-none'
          >
            I have read and understand this policy, and I acknowledge my responsibility to comply
            with its requirements.
          </label>
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => handleOpenChange(false)}
            disabled={isAcknowledging}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!hasRead || isAcknowledging}
            className='bg-green-600 hover:bg-green-700'
          >
            {isAcknowledging ? 'Acknowledging...' : 'Acknowledge & Confirm'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AcknowledgeReviewDialog;
